import React, { useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import {
  getComponentNameSelector,
  getDataLoadedSelector,
  getDataLoadingSelector,
  getDialogAskConfirmationSelector,
  getDialogTitleAppenderSelector,
  getDialogTitleSelector,
  getShowDialogSelector,
} from './selectors';
import { useInjectReducer } from '../../store/injectReducer';
import { ENTITY_DIALOG_REDUCER_KEY } from './constants';
import Typography from '@material-ui/core/Typography';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import Dialog from '@material-ui/core/Dialog/Dialog';
import entityDialogReducer from './reducer';
import { useDispatch, useSelector } from 'react-redux';
import { setEntityDialogVisibilityStatusAction } from './actions';
import useStyles from './styles';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import { ENTITY_DIALOG_COMPONENT_MAP } from './loadable';
import { CloseIcon } from '../../icons';
import { useInjectSaga } from '../../store/injectSaga';
import entityDialogRootSaga from './saga';
import { useIntl } from 'react-intl';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { pushToGtm } from '../../utils/datalayer';

const stateSelector = createStructuredSelector({
  dataLoading: getDataLoadingSelector,
  isLoaded: getDataLoadedSelector,
  componentName: getComponentNameSelector,
  showDialog: getShowDialogSelector,
  dialogTitle: getDialogTitleSelector,
  titleAppended: getDialogTitleAppenderSelector,
  askConfirmation: getDialogAskConfirmationSelector,
});

const EntityContainerDialog = () => {
  useInjectReducer({ key: ENTITY_DIALOG_REDUCER_KEY, reducer: entityDialogReducer });
  useInjectSaga({ key: ENTITY_DIALOG_REDUCER_KEY, saga: entityDialogRootSaga });

  const classes = useStyles();
  const dispatch = useDispatch();
  const { dataLoading, isLoaded, componentName, showDialog, dialogTitle, titleAppended, askConfirmation } = useSelector(
    stateSelector,
  );
  const intl = useIntl();

  const [showConfirmationDialog, setConfirmationDialog] = useState(false);

  const openConfirmDialog = () => setConfirmationDialog(true);
  const closeConfirmDialog = () => setConfirmationDialog(false);

  const handleDeleteConfirm = () => {
    fireActionToCloseDialog();
  };

  const fireActionToCloseDialog = () => {
    closeConfirmDialog();
    dispatch(setEntityDialogVisibilityStatusAction.Trigger({ show: false }));
  };
  const handleDialogClose = () => {
    if (askConfirmation) {
      openConfirmDialog();
    } else {
      fireActionToCloseDialog();
    }
  };

  useEffect(() => {
    if (showDialog && !dataLoading && !isLoaded) {
      setTimeout(handleDialogClose, 3000);
    }
  }, [dataLoading, isLoaded]);

  useEffect(() => {
    closeConfirmDialog();
  }, [showDialog]);

  useEffect(() => {
    if (componentName && typeof componentName === 'string') {
      pushToGtm(showDialog ? 'DialogOpen' : 'DialogClose', { dialog_name: componentName });
    }
  }, [componentName, showDialog]);

  const showComponent = dataLoading ? false : isLoaded;
  const showError = dataLoading ? false : !showComponent;

  const ComponentToRender = ENTITY_DIALOG_COMPONENT_MAP[componentName] || null;

  if (!ComponentToRender || !showDialog) {
    return null;
  }

  return (
    <>
      <Dialog className={classes.dialogRoot} onClose={handleDialogClose} open={showDialog}>
        <div className={classes.dialogTitleWrapper}>
          <Typography className={classes.dialogTitle} variant="h2">
            {intl.formatMessage({ id: dialogTitle, defaultMessage: dialogTitle })}
            {titleAppended ? ` | ${titleAppended}` : ''}
          </Typography>
          <div className={classes.closeDialogButton} onClick={handleDialogClose}>
            <CloseIcon />
          </div>
        </div>
        <DialogContent className={classes.dialogContentRoot}>
          {dataLoading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : null}
          {showError ? (
            <div className={classes.loading}>
              {intl.formatMessage({
                id: 'app.dialogs.error.loading',
                defaultMessage: 'Failed to Load Data. Closing...',
              })}
            </div>
          ) : null}
          {showComponent ? <ComponentToRender /> : null}
        </DialogContent>
      </Dialog>
      <Dialog
        className={classes.deleteComponentDialogWrapper}
        onClose={closeConfirmDialog}
        open={showConfirmationDialog}>
        <Typography variant="h2">
          {intl.formatMessage({ id: `entity.dialog.confirm.close.title`, defaultMessage: 'Confirm Leaving' })}
        </Typography>
        <DialogContent>
          <Typography variant="body1">
            {intl.formatMessage({
              id: 'entity.dialog.confirm.close.message',
              defaultMessage: `You will lose all the progress you've made so far. \nAre you sure you want to do this?`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={closeConfirmDialog}>
            {intl.formatMessage({ id: 'app.table.action.delete.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button autoFocus color="primary" onClick={handleDeleteConfirm}>
            {intl.formatMessage({ id: 'app.table.action.delete.confirm', defaultMessage: 'Confirm' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EntityContainerDialog;
